import type { ExtractedDocument } from "./import-schemas";
import { cleanText, looseKey } from "./menu-words";
import { parsePriceInput } from "./money";
import { normalizeForSearch } from "./slug";

// Deterministic merge of extraction results into draft content (ARCHITECTURE §7.2 point 3). No AI here and no
// guessing: a price is taken only when the printed text holds exactly one usable amount; everything uncertain
// becomes a review flag (critical ones block publishing, see Severity). Nothing is ever deleted.

/**
 * How much a question matters (owner, 2026-10-23): "critical" ones (a price that is missing, contradictory or read
 * only once, a word that could not be read) block publishing; "important" ones (a name spelled two ways, an
 * uncertain portion, a possible duplicate) are shown but never block — the restaurant's collective confirmation
 * covers them, and an uncertain portion is then left out rather than published as certain.
 */
export type Severity = "critical" | "important";

export type ReviewFlag =
  /**
   * The readings did not agree on the name (`candidates`: the other spellings), or only one reading found the
   * product (no candidates): it may be misread, or not exist on the menu at all.
   */
  (
    | { code: "name_unclear"; raw: string; candidates: string[] }
    /** `sizes`: the product is printed with several sizes; the review completes them in the editor. */
    | { code: "price_ambiguous"; raw: string; candidates: number[]; sizes?: string[] }
    | { code: "price_missing"; raw: string; sizes?: string[] }
    | { code: "possible_duplicate"; of: string }
    /** A portion breakdown that does not add up to the printed total, or a portion read with doubt. */
    | { code: "portion_unclear"; raw: string; total?: string; sum?: string }
    /** The price was read once only (no second reading found the dish): the restaurant confirms it. */
    | { code: "price_unverified"; raw: string; candidates: number[] }
    /** Re-import into an existing menu (owner 2026-11-03): a product the new document adds. */
    | { code: "import_new"; raw: string }
    /** Re-import: what the new document changed on an existing product ("Preț: 28,00 → 30,00"…). */
    | { code: "import_changed"; raw: string }
    /** Re-import: an existing product the new document no longer has (kept until the restaurant decides). */
    | { code: "import_missing"; raw: string }
  ) & { severity?: Severity };

/** Flags stored before severities existed count as critical (they used to block publishing). */
export function flagSeverity(f: ReviewFlag): Severity {
  if (f.severity) return f.severity;
  switch (f.code) {
    case "price_missing":
    case "price_ambiguous":
    case "price_unverified":
      return "critical";
    case "name_unclear":
      return f.raw.includes("?") ? "critical" : "important";
    case "import_new":
    case "import_changed":
    case "import_missing":
      return "important";
    default:
      return "important";
  }
}

/** SQL condition: the product has a question that blocks publishing (see flagSeverity). */
export const CRITICAL_FLAG_SQL = `EXISTS (SELECT 1 FROM json_each(review_flags_json)
  WHERE COALESCE(json_extract(value, '$.severity'), 'critical') = 'critical')`;

export const REVIEW_LABEL: Record<ReviewFlag["code"], string> = {
  name_unclear: "Nume de verificat",
  price_ambiguous: "Preț ambiguu",
  price_missing: "Preț lipsă",
  possible_duplicate: "Posibil duplicat",
  portion_unclear: "Gramaj de verificat",
  price_unverified: "Preț de confirmat",
  import_new: "Nou în meniul importat",
  import_changed: "Modificat la import",
  import_missing: "Lipsește din meniul importat",
};

export function reviewText(f: ReviewFlag): string {
  switch (f.code) {
    case "name_unclear":
      return f.candidates.length
        ? `Numele a fost citit diferit: „${f.raw}” sau ${f.candidates.map((c) => `„${c}”`).join(", ")}. Alege varianta din meniu sau scrie numele corect.`
        : `Produs găsit de o singură citire: verifică dacă „${f.raw}” apare în meniu și dacă numele este scris corect.`;
    case "price_ambiguous":
      return `Preț ambiguu în document: „${f.raw}”. Alege prețul corect.`;
    case "price_missing":
      return f.raw
        ? `Prețul nu a putut fi citit: „${f.raw}”. Introdu-l manual.`
        : "Prețul lipsește din document.";
    case "possible_duplicate":
      return `Posibil duplicat: „${f.of}” apare deja în această categorie.`;
    case "price_unverified":
      return `Prețul „${f.raw}” a fost citit o singură dată. Confirmă-l sau corectează-l.`;
    case "import_new":
      return "Produs nou: nu exista în meniu înainte de acest import.";
    case "import_changed":
      return `Actualizat din meniul importat: ${f.raw}.`;
    case "import_missing":
      return "Nu mai apare în meniul importat. Îl păstrezi sau îl scoți din meniu?";
    case "portion_unclear":
      return f.total && f.sum
        ? `Gramaj de verificat: „${f.raw}”. Componentele însumează ${f.sum}, iar totalul tipărit este ${f.total}.`
        : `Gramajul nu a putut fi citit sigur: „${f.raw}”. Verifică-l în document.`;
  }
}

// Portion units → grams / millilitres (ml counted like g: menus mix them in one breakdown, "250 g / 50 ml").
const PORTION_UNIT: Record<string, number> = { g: 1, gr: 1, kg: 1000, ml: 1, cl: 10, dl: 100, l: 1000 };
const PORTION_TOTAL = /^\s*(\d+(?:[.,]\d{1,3})?)\s*(kg|gr|g|ml|cl|dl|l)\b/i;

/** Numbers of a breakdown ("200/100/50 g", "200,100,50"): a comma is a decimal mark only in "1,5" or "0,33". */
function breakdownNumbers(text: string): { value: number; unit?: string }[] {
  const out: { value: number; unit?: string }[] = [];
  for (const m of text.matchAll(/(\d+)(?:[.,](\d+))?\s*(kg|gr|g|ml|cl|dl|l)?\b/gi)) {
    const [, int = "", frac, unit] = m;
    if (frac !== undefined && int.length <= 2 && frac.length <= 2) {
      out.push({ value: Number(`${int}.${frac}`), unit: unit?.toLowerCase() });
    } else if (frac !== undefined) {
      out.push({ value: Number(int) }, { value: Number(frac), unit: unit?.toLowerCase() });
    } else {
      out.push({ value: Number(int), unit: unit?.toLowerCase() });
    }
  }
  return out;
}

const grams = (value: number, unit: string) => Math.round(value * (PORTION_UNIT[unit] ?? 1) * 10) / 10;
const shown = (value: number, unit: string) =>
  `${String(Math.round(value * 10) / 10).replace(".", ",")} ${unit === "ml" || unit === "cl" || unit === "dl" || unit === "l" ? "ml" : "g"}`;

/** The printed total of a portion ("350 g" in "350 g (200/100/30 g)"), as written; null without one. */
export function portionTotal(portion: string): string | null {
  return PORTION_TOTAL.exec(portion)?.[0].trim() ?? null;
}

/**
 * Deterministic check of a printed portion (DEC-064: breakdowns are often misread). Only what can be shown
 * wrong is flagged: a breakdown whose parts do not add up to the printed total ("350 g (200/100/30 g)"), or a
 * reading marked as doubtful ("?"). A breakdown without a total cannot be checked and is left as read. Values
 * are never changed or completed here; the restaurant confirms or corrects them.
 */
export function portionCheck(portion: string): ReviewFlag | null {
  const raw = portion.trim();
  if (!raw) return null;
  if (raw.includes("?")) return { code: "portion_unclear", raw };
  const total = PORTION_TOTAL.exec(raw);
  if (!total) return null;
  const rest = raw.slice(total[0].length);
  // Counts and multiplications ("2 x 150 g", "6 buc") are not breakdowns.
  if (/\d\s*[x×*]\s*\d|\bx\s*\d|\bbuc|\bpers|\bporți|\bportii/i.test(rest)) return null;
  const parts = breakdownNumbers(rest);
  if (parts.length < 2) return null;
  const totalUnit = (total[2] ?? "g").toLowerCase();
  const totalValue = grams(Number((total[1] ?? "0").replace(",", ".")), totalUnit);
  // A part without its own unit takes the unit written after the last part ("200/100/50 g"), else the total's.
  const trailing = [...parts].reverse().find((p) => p.unit)?.unit ?? totalUnit;
  const sum = Math.round(parts.reduce((a, p) => a + grams(p.value, p.unit ?? trailing), 0) * 10) / 10;
  if (Math.abs(sum - totalValue) <= 1) return null;
  return { code: "portion_unclear", raw, total: shown(totalValue, totalUnit), sum: shown(sum, totalUnit) };
}

export type ParsedPrice =
  | { kind: "ok"; minor: number }
  | { kind: "ambiguous"; candidates: number[] }
  | { kind: "missing" };

// Amounts followed by a unit are quantities ("350 g", "2 buc"), not prices.
const QUANTITY_UNIT = /^\s*(g|gr|kg|mg|ml|cl|dl|l|buc|bucăți|bucati|cm|pers|persoane|porții|portii|x)\b/i;
const AMOUNT = /\d+(?:[.,]\d{1,2})?/g;

/** Every distinct amount in printed price text (minor units), quantities like "350 g" excluded. */
export function priceAmounts(raw: string | undefined): number[] {
  const text = (raw ?? "").replace(/\b(lei|ron|eur|euro)\b|€/gi, " ");
  const amounts: number[] = [];
  for (const m of text.matchAll(AMOUNT)) {
    const after = text.slice((m.index ?? 0) + m[0].length);
    if (QUANTITY_UNIT.test(after)) continue;
    const p = parsePriceInput(m[0]);
    if (p.ok && !amounts.includes(p.minor)) amounts.push(p.minor);
  }
  return amounts;
}

/** Reads printed price text without guessing: one amount → price; several → ambiguous; none → missing. */
export function parsePriceText(raw: string | undefined): ParsedPrice {
  const amounts = priceAmounts(raw);
  if (amounts.length === 1 && amounts[0] !== undefined) return { kind: "ok", minor: amounts[0] };
  if (amounts.length > 1) return { kind: "ambiguous", candidates: amounts.slice(0, 5) };
  return { kind: "missing" };
}

export interface MergeSource {
  fileId: string;
  doc: ExtractedDocument;
  /** Page of the original document where this file starts (a page prepared in the browser is one page). */
  firstPage?: number;
}

export interface ExistingContent {
  categories: { id: string; name: string; sortOrder: number }[];
  products: { categoryId: string; name: string; sortOrder: number }[];
}

export interface PlannedCategory {
  id: string;
  name: string;
  sortOrder: number;
  /** English heading printed on the menu (stored as an automatic translation to approve). */
  en?: string;
}

export interface PlannedProduct {
  id: string;
  categoryId: string;
  sortOrder: number;
  name: string;
  description: string;
  portion: string;
  priceMinor: number | null;
  variants: { label: string; priceMinor: number }[] | null;
  flags: ReviewFlag[];
  source: { fileId: string; page: number; text: string };
  /** English name/description printed on the menu (stored as an automatic translation to approve). */
  en?: { name: string; description: string };
  /** What was fixed without asking (kept with the provenance, shown in the review history). */
  autoFixes: string[];
}

export interface MergePlan {
  /** Only categories that do not exist yet in the target menu. */
  categories: PlannedCategory[];
  products: PlannedProduct[];
}

const clean = (s: string | undefined, max: number) => cleanText(s ?? "").slice(0, max);

/** Headings of an allergen list ("Alergeni", "Allergeni", "Allergens", "Lista alergenilor"), diacritics removed. */
const ALLERGEN_HEADING = /^(lista\s+)?(alergen|allergen)/;
/** A variant label that only names a language ("ro", "EN", "română", "English"). */
const LANGUAGE_LABEL = /^(ro|en|rom[aâ]n[aă]?|english|engleză|engleza)$/i;

type ExtractedProduct = ExtractedDocument["categories"][number]["products"][number];

function pricing(p: ExtractedProduct): Pick<PlannedProduct, "priceMinor" | "variants" | "portion" | "flags"> {
  const portion = clean(p.portion, 40);
  let variants = (p.variants ?? []).filter((v) => clean(v.label, 40));
  // The same price printed in each language ("ro 44 / en 44") is one price, not two sizes (Joben, 2026-10-27).
  if (
    variants.length >= 2 &&
    variants.every((v) => LANGUAGE_LABEL.test(clean(v.label, 40))) &&
    new Set(variants.map((v) => JSON.stringify(priceAmounts(v.priceText)))).size === 1
  )
    variants = variants.slice(0, 1).map((v) => ({ ...v, label: "" }));
  if (variants.length >= 2) {
    const parsed = variants.map((v) => ({ label: clean(v.label, 40), price: parsePriceText(v.priceText) }));
    if (parsed.every((v) => v.price.kind === "ok")) {
      return {
        portion,
        priceMinor: null,
        variants: parsed.map((v) => ({ label: v.label, priceMinor: (v.price as { minor: number }).minor })),
        flags: [],
      };
    }
    // Not every size has exactly one readable price: keep the printed text for the review, set no price.
    const raw = variants.map((v) => `${clean(v.label, 40)} ${clean(v.priceText, 60)}`.trim()).join("; ");
    const ambiguous = parsed.some((v) => v.price.kind === "ambiguous");
    const sizes = parsed.map((v) => v.label);
    return {
      portion,
      priceMinor: null,
      variants: null,
      flags: [
        ambiguous
          ? {
              code: "price_ambiguous",
              raw,
              candidates: parsed
                .flatMap((v) =>
                  v.price.kind === "ok"
                    ? [v.price.minor]
                    : v.price.kind === "ambiguous"
                      ? v.price.candidates
                      : [],
                )
                .slice(0, 5),
              sizes,
            }
          : { code: "price_missing", raw, sizes },
      ],
    };
  }
  // A single listed size is a single price; its label describes the portion.
  const single = variants[0];
  const priceText = single ? single.priceText : p.priceText;
  const price = parsePriceText(priceText);
  const withPortion = portion || clean(single?.label, 40);
  if (price.kind === "ok")
    return { portion: withPortion, priceMinor: price.minor, variants: null, flags: [] };
  const raw = clean(priceText, 60);
  return {
    portion: withPortion,
    priceMinor: null,
    variants: null,
    flags: [
      price.kind === "ambiguous"
        ? { code: "price_ambiguous", raw, candidates: price.candidates }
        : { code: "price_missing", raw },
    ],
  };
}

/**
 * Price double-check (owner decision 2026-10-22, after the first pilot call read "21 lei" as "24"): a second,
 * independent reading of the prices must agree with the first one. Any disagreement, or a product the second
 * reading did not find, becomes a price question with both readings as candidates. Never resolved by guessing.
 */
function checked(
  p: ExtractedProduct,
  base: Pick<PlannedProduct, "priceMinor" | "variants" | "portion" | "flags">,
): Pick<PlannedProduct, "priceMinor" | "variants" | "portion" | "flags"> {
  if (p.priceCheck === undefined || base.flags.length) return base;
  const first = base.variants
    ? base.variants.map((v) => v.priceMinor)
    : base.priceMinor === null
      ? []
      : [base.priceMinor];
  const printed = base.variants
    ? base.variants.map((v) => `${v.label} ${(v.priceMinor / 100).toFixed(2)}`).join("; ")
    : clean(p.priceText || p.variants?.[0]?.priceText, 60);
  // An empty second reading found no price for the product: the same as not finding it, never a contradiction.
  const notFound = p.priceCheck.trim() === "?" || !priceAmounts(p.priceCheck).length;
  const second = notFound ? [] : priceAmounts(p.priceCheck);
  const same = !notFound && second.length === first.length && first.every((a) => second.includes(a));
  if (same) return base;
  // Read once, nothing contradicts it: one click to confirm (it is not a contradiction to choose from).
  if (notFound && first.length === 1 && !base.variants)
    return {
      ...base,
      priceMinor: null,
      flags: [{ code: "price_unverified", raw: printed, candidates: first }],
    };
  return {
    portion: base.portion,
    priceMinor: null,
    variants: null,
    flags: [
      {
        code: "price_ambiguous",
        raw: notFound
          ? `${printed} (a doua citire nu a găsit produsul)`
          : `${printed} / a doua citire: ${clean(p.priceCheck, 60)}`,
        candidates: [...new Set([...first, ...second])].slice(0, 5),
        ...(base.variants ? { sizes: base.variants.map((v) => v.label) } : {}),
      },
    ],
  };
}

function bigrams(s: string): string[] {
  const t = ` ${s} `;
  return [...t].slice(0, -1).map((_, i) => t.slice(i, i + 2));
}

/** Dice coefficient on character pairs (0…1): tolerant to one misread letter, strict on different products. */
export function similarity(a: string, b: string): number {
  if (a === b) return 1;
  const x = bigrams(a);
  const y = bigrams(b);
  let common = 0;
  const rest = [...y];
  for (const g of x) {
    const i = rest.indexOf(g);
    if (i >= 0) {
      common++;
      rest.splice(i, 1);
    }
  }
  return (2 * common) / (x.length + y.length);
}

/**
 * Attaches the second reading's prices to the products of the first reading (deterministic matching by name:
 * exact normalised name first, then the most similar unused name with similarity ≥ 0.8). Unmatched products get
 * "?" so the merge asks the restaurant to confirm their price.
 */
export function attachPriceCheck(
  doc: ExtractedDocument,
  items: { name: string; prices: string[] }[],
): ExtractedDocument {
  const pool = items.map((it) => ({
    key: normalizeForSearch(it.name),
    text: it.prices.join(" / "),
    used: false,
  }));
  const take = (key: string) => {
    let best: (typeof pool)[number] | undefined = pool.find((it) => !it.used && it.key === key);
    if (!best) {
      let score = 0.8;
      for (const it of pool) {
        const s = it.used ? 0 : similarity(it.key, key);
        if (s >= score) {
          score = s;
          best = it;
        }
      }
    }
    if (best) best.used = true;
    return best?.text;
  };
  return {
    ...doc,
    categories: doc.categories.map((c) => ({
      ...c,
      products: c.products.map((p) => ({ ...p, priceCheck: take(normalizeForSearch(p.name)) ?? "?" })),
    })),
  };
}

/**
 * Plans the draft content for new extraction results. Categories with the same name (ignoring case, diacritics
 * and singular / plural: "PIZZA" and "PIZZE") are merged, also with categories already in the target menu. A
 * product whose name repeats within a category with another portion and another price is one product in two
 * sizes (variants: "ARROGANCE 150 ml 25 lei" and "ARROGANCE 750 ml 120 lei"); otherwise it is kept and flagged as
 * a possible duplicate. Order follows the documents.
 */
export function planMerge(sources: MergeSource[], existing: ExistingContent, newId: () => string): MergePlan {
  const categories: PlannedCategory[] = [];
  const products: PlannedProduct[] = [];
  const byKey = new Map<string, { id: string; name: string }>();
  const nextSort = new Map<string, number>();
  // categoryId → name key → the first product with that name (planned now, or only its name when it exists)
  const seen = new Map<string, Map<string, PlannedProduct | string>>();
  let nextCategorySort = Math.max(-1, ...existing.categories.map((c) => c.sortOrder)) + 1;
  const withSeverity = (f: ReviewFlag): ReviewFlag => ({ ...f, severity: flagSeverity(f) });

  for (const c of existing.categories) {
    byKey.set(looseKey(c.name), { id: c.id, name: c.name });
    seen.set(c.id, new Map());
  }
  for (const p of existing.products) {
    seen.get(p.categoryId)?.set(looseKey(p.name), p.name);
    nextSort.set(p.categoryId, Math.max(nextSort.get(p.categoryId) ?? 0, p.sortOrder + 1));
  }

  for (const { fileId, doc, firstPage } of sources) {
    for (const c of doc.categories) {
      const name = clean(c.name, 80);
      const key = looseKey(name);
      // An allergen list printed on the menu is information, never a section of dishes (Joben, 2026-10-27).
      if (!key || !c.products.length || ALLERGEN_HEADING.test(normalizeForSearch(name))) continue;
      let cat = byKey.get(key);
      if (!cat) {
        cat = { id: newId(), name };
        byKey.set(key, cat);
        seen.set(cat.id, new Map());
        const en = clean(c.en, 80);
        categories.push({ id: cat.id, name, sortOrder: nextCategorySort++, ...(en ? { en } : {}) });
      }
      const names = seen.get(cat.id) ?? new Map<string, PlannedProduct | string>();
      for (const p of c.products) {
        const productName = clean(p.name, 150);
        const nameKey = looseKey(productName);
        if (!nameKey) continue;
        const priced = checked(p, pricing(p));
        const autoFixes = (p.autoFixes ?? []).map((x) => clean(x, 200));
        if (cat.name !== name) autoFixes.push(`Categoria „${name}” este aceeași cu „${cat.name}”.`);
        const first = names.get(nameKey);
        // The same name with another portion and another price, both read clearly: one product in two sizes.
        if (
          typeof first === "object" &&
          !first.flags.length &&
          !priced.flags.length &&
          first.priceMinor !== null &&
          priced.priceMinor !== null &&
          first.priceMinor !== priced.priceMinor &&
          first.portion &&
          priced.portion &&
          looseKey(first.portion) !== looseKey(priced.portion)
        ) {
          first.variants = [
            { label: first.portion, priceMinor: first.priceMinor },
            { label: priced.portion, priceMinor: priced.priceMinor },
          ];
          first.priceMinor = null;
          first.portion = "";
          first.autoFixes.push(
            `Două mărimi ale aceluiași produs, unite ca variante (${first.variants.map((v) => v.label).join(" / ")}).`,
          );
          continue;
        }
        if (
          typeof first === "object" &&
          first.variants &&
          priced.priceMinor !== null &&
          priced.portion &&
          !priced.flags.length
        ) {
          first.variants.push({ label: priced.portion, priceMinor: priced.priceMinor });
          continue;
        }
        // A portion the independent readings did not confirm is asked about with both readings.
        const portionFlag: ReviewFlag | null =
          p.portionCheck !== undefined && priced.portion
            ? {
                code: "portion_unclear",
                raw:
                  clean(p.portionCheck, 40) && clean(p.portionCheck, 40) !== priced.portion
                    ? `${priced.portion} / a doua citire: ${clean(p.portionCheck, 40)}`
                    : priced.portion,
              }
            : portionCheck(priced.portion);
        const firstName = typeof first === "object" ? first.name : first;
        const flags: ReviewFlag[] = [
          // The name first: a dish that is not on the menu needs no price.
          ...(p.nameCheck
            ? [
                {
                  code: "name_unclear" as const,
                  raw: productName,
                  candidates: p.nameCheck.map((n) => clean(n, 150)).filter((n) => n && n !== productName),
                },
              ]
            : []),
          ...priced.flags,
          ...(portionFlag ? [portionFlag] : []),
          ...(firstName ? [{ code: "possible_duplicate" as const, of: firstName }] : []),
        ].map(withSeverity);
        const sortOrder = nextSort.get(cat.id) ?? 0;
        nextSort.set(cat.id, sortOrder + 1);
        const product: PlannedProduct = {
          id: newId(),
          categoryId: cat.id,
          sortOrder,
          name: productName,
          description: clean(p.description, 1000),
          ...priced,
          flags,
          source: { fileId, page: (firstPage ?? 1) + p.source.page - 1, text: clean(p.source.text, 500) },
          ...(p.en && clean(p.en.name, 150)
            ? { en: { name: clean(p.en.name, 150), description: clean(p.en.description, 1000) } }
            : {}),
          autoFixes: autoFixes.slice(0, 10),
        };
        if (!first) names.set(nameKey, product);
        products.push(product);
      }
    }
  }
  return { categories, products };
}

/** An existing product of the menu a re-import updates. */
export interface ExistingProduct {
  id: string;
  name: string;
  priceMinor: number | null;
  variants: { label: string; priceMinor: number }[] | null;
  portion: string;
  description: string;
}

export interface ReimportUpdate {
  id: string;
  priceMinor?: number | null;
  variants?: { label: string; priceMinor: number }[] | null;
  portion?: string;
  description?: string;
  flags: ReviewFlag[];
}

export interface ReimportPlan {
  /** Products the new document adds (flagged "import_new"). */
  inserts: PlannedProduct[];
  /** Existing products the document changed (draft updates, flagged "import_changed"). */
  updates: ReimportUpdate[];
  unchanged: number;
  /** Existing products the document no longer has (flagged "import_missing", never deleted here). */
  missing: string[];
}

const amount = (minor: number) => (minor / 100).toFixed(2).replace(".", ",");
const PRICE_CODES = new Set(["price_missing", "price_ambiguous", "price_unverified"]);

/**
 * Re-import into an existing menu (owner 2026-11-03): matches the planned products to the menu's products by name
 * (the same loose key as duplicates). Unchanged products disappear from the review; changes go into the draft and
 * say what changed; new products are added; products the document no longer has are only flagged. A price the
 * import could not read for sure never overwrites the current price: its question goes on the product instead.
 */
export function planReimport(plan: MergePlan, existing: ExistingProduct[]): ReimportPlan {
  const byKey = new Map<string, ExistingProduct>();
  for (const e of existing) if (!byKey.has(looseKey(e.name))) byKey.set(looseKey(e.name), e);
  const matched = new Set<string>();
  const inserts: PlannedProduct[] = [];
  const updates: ReimportUpdate[] = [];
  let unchanged = 0;
  for (const p of plan.products) {
    const e = byKey.get(looseKey(p.name));
    if (!e || matched.has(e.id)) {
      inserts.push({
        ...p,
        flags: [
          ...p.flags.filter((f) => f.code !== "possible_duplicate"),
          { code: "import_new", raw: p.name, severity: "important" },
        ],
      });
      continue;
    }
    matched.add(e.id);
    const priceQuestions = p.flags.filter((f) => PRICE_CODES.has(f.code));
    const update: ReimportUpdate = { id: e.id, flags: [] };
    const changes: string[] = [];
    if (!priceQuestions.length) {
      if (p.variants || e.variants) {
        if (p.variants && JSON.stringify(p.variants) !== JSON.stringify(e.variants)) {
          update.variants = p.variants;
          update.priceMinor = null;
          changes.push(`Prețuri: ${p.variants.map((v) => `${v.label} ${amount(v.priceMinor)}`).join(" / ")}`);
        }
      } else if (p.priceMinor !== null && p.priceMinor !== e.priceMinor) {
        update.priceMinor = p.priceMinor;
        changes.push(`Preț: ${e.priceMinor === null ? "—" : amount(e.priceMinor)} → ${amount(p.priceMinor)}`);
      }
    } else update.flags.push(...priceQuestions);
    if (p.portion && p.portion !== e.portion) {
      update.portion = p.portion;
      changes.push(`Porție: ${e.portion || "—"} → ${p.portion}`);
    }
    if (p.description && p.description !== e.description) {
      update.description = p.description;
      changes.push("Descriere actualizată");
    }
    if (!changes.length && !update.flags.length) {
      unchanged++;
      continue;
    }
    if (changes.length)
      update.flags.push({ code: "import_changed", raw: changes.join(" · "), severity: "important" });
    updates.push(update);
  }
  const missing = existing.filter((e) => !matched.has(e.id)).map((e) => e.id);
  return { inserts, updates, unchanged, missing };
}
